import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Send, Clock, CheckCircle2, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  adminListNewsletters,
  adminDeleteNewsletter,
  adminUnscheduleNewsletter,
} from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  draft: { label: "Draft", icon: FileText, className: "bg-paper-200 text-ink" },
  scheduled: { label: "Scheduled", icon: Clock, className: "bg-scholar text-ink" },
  sending: { label: "Sending…", icon: Send, className: "bg-terracotta text-paper-50" },
  sent: { label: "Sent", icon: CheckCircle2, className: "bg-forest text-paper-50" },
};

export default function AdminNewsletters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListNewsletters());
    } catch {
      toast.error("Couldn't load newsletters.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // refresh every 10s (for sending status)
    return () => clearInterval(t);
  }, []);

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminDeleteNewsletter(deleteTarget.id);
      toast.success("Deleted.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Delete failed.");
    }
  };

  const onUnschedule = async (id) => {
    try {
      await adminUnscheduleNewsletter(id);
      toast.success("Unscheduled and moved back to draft.");
      load();
    } catch {
      toast.error("Unschedule failed.");
    }
  };

  return (
    <div data-testid="admin-newsletters-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta">Newsletter</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink mt-2">
            Your newsletters
          </h1>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/subscribers"
            data-testid="admin-subscribers-link"
            className="inline-flex items-center gap-2 bg-paper-100 border border-paper-200 text-ink px-5 py-3 text-sm font-semibold rounded-sm hover:bg-paper-200 transition-colors"
          >
            Subscribers
          </Link>
          <Link
            to="/admin/newsletter/new"
            data-testid="admin-newsletter-new-link"
            className="inline-flex items-center gap-2 bg-forest text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> New newsletter
          </Link>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-24 bg-paper-100 border border-paper-200 rounded-sm animate-pulse" />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-paper-100 border border-paper-200 p-10 rounded-sm text-center">
          <p className="text-ink-muted">No newsletters yet. Click "New newsletter" to start.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-3" data-testid="admin-newsletters-list">
          {items.map((n) => {
            const meta = STATUS_META[n.status] || STATUS_META.draft;
            const Icon = meta.icon;
            return (
              <article
                key={n.id}
                data-testid={`admin-newsletter-row-${n.id}`}
                className="bg-paper-100 border border-paper-200 p-5 rounded-sm flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${meta.className}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.from_subtitle && (
                      <span className="text-[11px] uppercase tracking-wider text-terracotta font-bold">
                        · {n.from_subtitle}
                      </span>
                    )}
                  </div>
                  <h2 className="font-display font-bold text-lg sm:text-xl text-ink mt-1.5 leading-snug truncate">
                    {n.subject}
                  </h2>
                  <p className="text-xs text-ink-muted mt-1">
                    {n.status === "sent" && `Sent to ${n.sent_count} subscribers · ${n.sent_at?.slice(0, 10)}`}
                    {n.status === "scheduled" && `Scheduled for ${new Date(n.scheduled_at).toLocaleString()}`}
                    {n.status === "draft" && `Saved ${n.updated_at?.slice(0, 10)}`}
                    {n.status === "sending" && "Delivering… this page auto-refreshes."}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {n.status === "scheduled" && (
                    <button
                      onClick={() => onUnschedule(n.id)}
                      data-testid={`newsletter-unschedule-${n.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-paper-50 border border-paper-200 rounded-sm hover:bg-scholar hover:border-scholar transition-colors"
                    >
                      Unschedule
                    </button>
                  )}
                  {n.status !== "sent" && n.status !== "sending" && (
                    <>
                      <Link
                        to={`/admin/newsletter/edit/${n.id}`}
                        data-testid={`newsletter-edit-${n.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-paper-50 border border-paper-200 rounded-sm hover:bg-forest hover:text-paper-50 hover:border-forest transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Link>
                      <button
                        onClick={() => setDeleteTarget(n)}
                        data-testid={`newsletter-delete-${n.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-paper-50 border border-paper-200 rounded-sm hover:bg-terracotta hover:text-paper-50 hover:border-terracotta transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </>
                  )}
                  {n.status === "sent" && (
                    <Link
                      to={`/admin/newsletter/edit/${n.id}`}
                      data-testid={`newsletter-view-${n.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-ink-muted hover:text-forest"
                    >
                      View
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="newsletter-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this newsletter?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.subject}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="newsletter-delete-confirm"
              className="bg-terracotta hover:bg-terracotta-dark text-paper-50"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
